/**
 * CruxPagesPanel — per-page CrUX field data in the Traffic "Top pages"
 * card idiom (mirrors BreakdownCard in RumPlaceholders.tsx): header +
 * search + column headers + banded rows + a metric footer toggle.
 *
 * One row per CrUX-covered page, joined with the pixel's own
 * views/sessions (from `useSiteTopPages`) on the URL path — so each row
 * shows traffic AND the Chrome UX Report field score/vital together.
 * The join is path-on-path (CrUX carries the full URL, pixel the path),
 * left from CrUX; views are `—` when the pixel hasn't seen the page (or
 * isn't installed).
 */
import {
  GEO_METRICS,
  type GeoMetric,
  bandQuality,
  geoBandOf,
  geoFormatValue,
} from '@/lib/crux-geo-metric';
import { bandText } from '@/lib/format';
import type { CruxPageRow, CruxPagesResponse } from '@/lib/queries/crux';
import { useSiteTopPages } from '@/lib/queries/pixel';
import { cn } from '@/lib/utils';
import { ChartBar, MagnifyingGlass } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

/** Traffic window for the views/sessions join — matches CrUX's 28-day
 *  rolling window so the two read on the same timescale. */
const JOIN_DAYS = 28;

interface Row extends CruxPageRow {
  path: string;
  views: number | null;
  sessions: number | null;
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

function formatN(n: number | null): string {
  if (n == null) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n));
}

export function CruxPagesPanel({
  siteUuid,
  pages,
  metric,
}: {
  siteUuid: string | null;
  pages?: CruxPagesResponse | undefined;
  metric: GeoMetric;
}): ReactElement | null {
  const [search, setSearch] = useState('');
  const top = useSiteTopPages(siteUuid, { days: JOIN_DAYS, limit: 200, enabled: !!siteUuid });

  const rows = useMemo<Row[]>(() => {
    if (!pages) return [];
    const byPath = new Map((top.data ?? []).map((r) => [r.url, r]));
    return pages.pages.map((p) => {
      const path = pathOf(p.url);
      const hit = byPath.get(path);
      return { ...p, path, views: hit?.pageviews ?? null, sessions: hit?.sessions ?? null };
    });
  }, [pages, top.data]);

  if (!pages) return null;

  const maxViews = Math.max(0, ...rows.map((r) => r.views ?? 0));
  const filtered = search
    ? rows.filter(
        (r) =>
          r.path.toLowerCase().includes(search.toLowerCase()) ||
          r.name.toLowerCase().includes(search.toLowerCase()),
      )
    : rows;
  // Most-trafficked first; CrUX-covered-but-untrafficked pages sink.
  const sorted = [...filtered].sort((a, b) => (b.views ?? -1) - (a.views ?? -1));

  return (
    <section className="flex flex-col rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-3 py-2.5">
        <div className="flex items-center gap-2">
          <ChartBar aria-hidden="true" size={14} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">Pages</h3>
        </div>
        <span className="text-[11px] text-[var(--color-mute)]">
          {pages.pages.length} of {pages.monitored_count} monitored in CrUX
        </span>
      </header>

      {pages.pages.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-[var(--color-ink-soft)]">
          None of this site’s monitored pages have individual Chrome UX Report coverage yet — Google
          only publishes page-level field data for individually popular URLs, so this is normal. The
          origin-level data above still covers the whole site.
        </p>
      ) : (
        <>
          <div className="relative border-b border-[var(--color-line-soft)]">
            <MagnifyingGlass
              aria-hidden="true"
              size={14}
              className="absolute top-1/2 left-3 -translate-y-1/2 text-[var(--color-mute)]"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search page…"
              className="block h-8 w-full bg-transparent pr-3 pl-8 text-xs text-[var(--color-ink-soft)] placeholder:text-[var(--color-mute)]/70 focus:outline-none"
            />
          </div>

          <div className="grid border-b border-[var(--color-line-soft)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)] [grid-template-columns:1fr_auto_auto]">
            <span>Page</span>
            <span className="w-14 text-right">Views</span>
            <span className="w-14 text-right text-[var(--color-ink-soft)]">
              {GEO_METRICS.find((m) => m.key === metric)?.label}
            </span>
          </div>

          <ul className="flex-1">
            {sorted.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-[var(--color-mute)]">
                No page matches “{search}”.
              </li>
            ) : (
              sorted.map((r) => {
                const band = geoBandOf(r, metric);
                const barPct =
                  maxViews > 0
                    ? Math.max(8, Math.round(((r.views ?? 0) / maxViews) * 100))
                    : Math.max(8, Math.round(bandQuality(band) * 100));
                return (
                  <li
                    key={r.page_uuid}
                    className="relative h-8 border-b border-[var(--color-line-soft)]/60 last:border-0"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 bg-[var(--color-bar)]/10"
                      style={{ width: `${barPct}%` }}
                    />
                    <div className="relative grid h-full items-center px-3 [grid-template-columns:1fr_auto_auto]">
                      <Link
                        to="/performance/audits"
                        search={{ page: r.page_uuid }}
                        title={r.url}
                        className="min-w-0 truncate text-xs text-[var(--color-ink-soft)] hover:text-[var(--color-accent-brand)] hover:underline"
                      >
                        {r.name || r.path}
                      </Link>
                      <span className="w-14 text-right text-xs tabular-nums text-[var(--color-ink-soft)]">
                        {formatN(r.views)}
                      </span>
                      <span
                        className={cn(
                          'w-14 text-right text-xs font-semibold tabular-nums',
                          bandText(band),
                        )}
                      >
                        {geoFormatValue(r, metric)}
                      </span>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </>
      )}
    </section>
  );
}
